import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Divider, Form, Input, message, Row, Select } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import { useForm } from 'antd/lib/form/Form';
import moment, { Moment } from 'moment';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, TransactionHistoryParams } from 'src/@types/search-params';
import { getOrderSims } from 'src/api/changeSim';
import CGridView from 'src/components/CGridView';
import TableContent from 'src/components/CTable';
import { usePermission } from 'src/hooks/usePermission';
import useWindowSize from 'src/hooks/useWindowSize';
import { OpenNewTab } from 'src/shared/common/openNewTab';
import { ChangeSimStatus } from 'src/shared/enum/change_sim';
import { SimType } from 'src/shared/enum/sim.enum';
import { OrderSim } from 'src/shared/interface/changeSim.interface';
import { TransactionHistory } from 'src/shared/interface/localpay.interface';
import { exportExcel, formatDateTime, formatMoney, parseQueryString } from 'src/utils';
import { simTypeToText } from 'src/utils/sim.enum';
import { useAppSelector } from 'src/redux/hook';

interface FormData {
  status?: string;
  dateRange?: [Moment, Moment];
  search?: string;
  simType?: string;
}
const listStatus = [
  { name: 'Tất cả', value: '-1' },
  { name: 'New', value: ChangeSimStatus.New },
  { name: 'Processing', value: ChangeSimStatus.Processing },
  // { name: 'Failed', value: ChangeSimStatus.Failed },
  { name: 'Completed', value: ChangeSimStatus.Completed },
  { name: 'Uncompleted', value: ChangeSimStatus.Uncompleted },
  { name: 'Paid', value: ChangeSimStatus.Paid },
  { name: 'Payment fail', value: ChangeSimStatus.PaymentFail },
  { name: 'Canceled', value: ChangeSimStatus.Canceled },
];
const listType = [
  { name: 'Tất cả', value: '-1' },
  { name: simTypeToText(SimType.PHYSICAL_SIM), value: SimType.PHYSICAL_SIM },
  { name: simTypeToText(SimType.ESIM), value: SimType.ESIM },
];

const ChangeSim = () => {
  const [form] = useForm();
  const { activeKeyId } = useAppSelector((state) => state.global);
  const size = useWindowSize();

  const [total, setTotal] = useState(0);
  const [data, setData] = useState<TransactionHistory[]>([]);
  const [sortColumn, setSortColumn] = useState<ISortColumn>();
  const [, setSearchParams] = useSearchParams();

  const { hasPermission, PERMISSIONS } = usePermission();

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current >= moment().endOf('day');
  };

  const renderDate = (date: string) => {
    return <div>{date ? formatDateTime(new Date(date)) : ''}</div>;
  };
  const goToDetail = (data: any) => {
    OpenNewTab('changeSim/createChangeSim' + '?id=' + data.id, 'Xem chi tiết đổi sim ' + data.id);
  };
  const renderAction = (data: OrderSim) => {
    return (
      <button
        onClick={() => hasPermission(PERMISSIONS.CHANGE_SIM.VIEW_DETAIL) && goToDetail(data)}
        className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
          !hasPermission(PERMISSIONS.CHANGE_SIM.VIEW_DETAIL) ? 'text-gray-400' : 'text-blue-400'
        } font-semibold`}
      >
        Xem chi tiết
      </button>
    );
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', align: 'center', isHideSort: true, width: 20 },
    {
      name: 'Mã đơn',
      fieldName: 'id',
      align: 'center',
      isHideSort: true,
      renderSpecialCard: (data) => (
        <span
          onClickCapture={() =>
            hasPermission(PERMISSIONS.CHANGE_SIM.VIEW_DETAIL) && goToDetail(data)
          }
          className={`underline text-blue-700 cursor-pointer ${
            !hasPermission(PERMISSIONS.CHANGE_SIM.VIEW_DETAIL)
              ? 'pointer-events-none opacity-50 text-gray-400'
              : ''
          }`}
        >
          {data.id}
        </span>
      ),
    },
    {
      name: 'Loại sim',
      fieldName: 'simType',
      align: 'center',
      isHideSort: true,
      renderSpecialCard: (data) => <span>{simTypeToText(data?.simType)}</span>,
    },
    { name: 'Trạng thái', fieldName: 'statusString', isHideSort: true, width: 100 },
    {
      name: 'Số giao dịch thanh toán',
      fieldName: 'transactionId',
      isHideSort: true,
      align: 'center',
      width: 150,
      renderSpecialCard: (data) => (
        <span className='whitespace-nowrap'>
          {data.toTalAmount !== 0 ? data.transactionId : ''}
        </span>
      ),
    },
    {
      name: 'Thành tiền',
      fieldName: 'toTalAmount',
      isHideSort: true,
      renderSpecialCard: (data) => (
        <span className='whitespace-nowrap'>{formatMoney(data.toTalAmount)}</span>
      ),
    },
    {
      name: 'Tổng cộng',
      fieldName: 'quantity',
      isHideSort: true,
      align: 'center',
      renderSpecialCard: (data) => <span className='whitespace-nowrap'>{data.countSerial}</span>,
    },
    {
      name: 'Thành công',
      fieldName: 'quantitySuccess',
      isHideSort: true,
      align: 'center',
    },
    {
      name: 'Thất bại',
      fieldName: 'quantityFailed',
      isHideSort: true,
      align: 'center',
    },
    {
      name: 'Người tạo',
      fieldName: 'createdBy',
      isHideSort: true,
      align: 'center',
    },
    {
      name: 'Ngày tạo',
      fieldName: 'createdAt',
      isHideSort: true,
      renderSpecialCard: (data) => renderDate(data.createdAt),
    },
    {
      name: 'Ngày cập nhật',
      fieldName: 'updatedAt',
      isHideSort: true,
      renderSpecialCard: (data) => renderDate(data.updatedAt),
    },
    {
      name: 'Người cập nhật',
      fieldName: 'updatedBy',
      isHideSort: true,
      align: 'center',
    },
    { name: 'Thao tác', fieldName: 'id', renderSpecialCard: renderAction, isHideSort: true },
  ];
  const userStore = localStorage.getItem('merchantInfo');
  let merchantInfo: any;
  if (userStore !== null) {
    try {
      merchantInfo = JSON.parse(userStore);
    } catch (error) {
      console.log(error);
    }
  }

  const renderOptions = (listOpts: any[]) => {
    return listOpts.map((item) => {
      return (
        <Select.Option value={item.value} key={item.value}>
          {item.name}
        </Select.Option>
      );
    });
  };

  const handleSearch = (formValues: FormData) => {
    const { status, search, dateRange, simType } = formValues;
    const searchParams = {
      status: status === '-1' ? undefined : status,
      simType:
        simType == '-1' || simType === 'Tất cả'
          ? undefined
          : listType.find((x) => x.value === simType)?.value ||
            listType.find((x) => x.name === simType)?.value,
      search: search?.trim() || undefined,
      pageIndex: 1,
      pageSize: 10,
      dateFrom: dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined,
      dateTo: dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined,
    };
    fetchData(searchParams as TransactionHistoryParams);
    setSortColumn(undefined);
    setSearchParams(queryString.stringify(searchParams));
  };

  const handleExport = () => {
    const pagination = parseQueryString(location.search);
    const params = queryString.stringify({ ...pagination, distributorId: merchantInfo.merchantId });
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `danh_sach_doi_sim_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
    const url = process.env.REACT_APP_API_URL + '/api/Provider/physical-sim/export?' + params;
    exportExcel(url)
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  };

  const fetchData = async (params = {}) => {
    const orderChangeSim = await getOrderSims(params);
    if (orderChangeSim) {
      setData(
        orderChangeSim.results.map((item: any, index: number) => {
          return { ...item, key: index };
        }),
      );
      setTotal(orderChangeSim.rowCount);
    }
  };

  useEffect(() => {
    const pagination = parseQueryString(location.search);
    fetchData();
    setSortColumn(undefined);
    form.setFieldsValue({
      ...pagination,
      simType:
        listType.find((x) => x.value === Number(pagination.simType))?.name ?? listType[0].name,
    });
  }, [location.search]);

  return (
    <>
      <div>
        <Form
          form={form}
          onFinish={handleSearch}
          initialValues={{ status: '-1', type: '-1' }}
          layout={size.width < 768 ? 'vertical' : 'inline'}
          className='gap-2'
        >
          <Form.Item name='search'>
            <Input
              allowClear
              className='md:min-w-[300px]'
              prefix={<SearchOutlined />}
              placeholder='Mã đơn hàng, Mã giao dịch'
            />
          </Form.Item>
          <Form.Item name='simType' label='Loại sim'>
            <Select showSearch className='min-w-[150px]'>
              {renderOptions(listType)}
            </Select>
          </Form.Item>
          <Form.Item name='status' label='Trạng thái'>
            <Select showSearch className='min-w-[150px]'>
              {renderOptions(listStatus)}
            </Select>
          </Form.Item>
          <Form.Item name='dateRange' label='Ngày tạo'>
            <DatePicker.RangePicker
              disabledDate={disabledDate}
              placeholder={['từ', 'đến']}
              format={['DD/MM/YYYY', 'DD/MM/YYYY']}
            />
          </Form.Item>

          <div className='row-button flex-wrap md:flex-nowrap md:justify-end gap-y-4 mb-6 lg:mb-0'>
            <Button htmlType='submit'>Tìm kiếm</Button>
            {hasPermission(PERMISSIONS.CHANGE_SIM.CREATE) && (
              <Button
                type='primary'
                onClick={() => {
                  OpenNewTab('changeSim/createChangeSim', 'Tạo mới đơn hàng đổi sim', true);
                }}
              >
                Tạo mới
              </Button>
            )}
            {hasPermission(PERMISSIONS.CHANGE_SIM.EXPORT_LIST) && (
              <Button type='primary' onClick={handleExport}>
                Export
              </Button>
            )}
          </div>
        </Form>
        <div className='mb-10 lg:mb-0 mt-8'>
          {size.width < 1024 ? (
            <CGridView
              length={data?.length}
              totalRecord={total ?? 0}
              columns={columns}
              title={() => <div className='text-xl font-bold mb-6'>Danh sách đơn đổi SIM</div>}
            >
              {data && data.length > 0 ? (
                data.map((item: TransactionHistory) => {
                  return (
                    <div key={'data-row-' + item.id}>
                      <Divider className='border-t-black' />

                      <Row className='mb-6 items-center' gutter={[4, 16]}>
                        {columns.map((ele) => {
                          if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                            return (
                              <>
                                <Col span={11} className={`font-bold label`}>
                                  <div className='flex gap-2 items-center'>{ele.name}</div>
                                </Col>
                                <Col className='break-words text-right' span={13}>
                                  {ele?.renderSpecialCard
                                    ? ele?.renderSpecialCard(item, null, null, null)
                                    : item[`${ele?.fieldName}`]}
                                </Col>
                              </>
                            );
                          } else if (ele.fieldName === 'action') {
                            return (
                              <Col key={'column-' + item.id + ele.fieldName} span={24}>
                                <div className='flex gap-6'>
                                  {ele?.renderSpecialCard
                                    ? ele?.renderSpecialCard(item, null, null, null)
                                    : item[`${ele?.fieldName}`]}
                                </div>
                              </Col>
                            );
                          } else {
                            return <></>;
                          }
                        })}
                      </Row>
                    </div>
                  );
                })
              ) : (
                <p>{'Không có dữ liệu'}</p>
              )}
            </CGridView>
          ) : (
            <TableContent
              locale={{ emptyText: 'Không tìm thấy kết quả' }}
              totalRecord={total}
              dataSource={data}
              columns={columns}
              sortColumn={sortColumn}
              onPageChange={(pageNumber, pageSize) => {
                const pagination = parseQueryString(location.search);
                fetchData({
                  ...pagination,
                  pageIndex: pageNumber ?? 1,
                  pageSize: pageSize ?? 10,
                  sortField: pagination.sortField ?? undefined,
                  asc: pagination.asc ?? undefined,
                });
              }}
              onSort={(sort: ISortColumn) => {
                const pagination = parseQueryString(location.search);
                fetchData({
                  ...pagination,
                  pageIndex: pagination.pageIndex ?? 1,
                  pageSize: pagination.pageSize ?? 10,
                  sortField: sort.fieldName,
                  asc:
                    sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? false : undefined,
                });
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ChangeSim;
